import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  ACTIVE_STATE_FILTER_VALUES,
  getSourceOptions,
  STATUS_FILTER_VALUES,
} from '../../containers/strategy/initiative-list/filters/filters.helpers';
import { CostMode } from '../../containers/strategy/initiative-performance/cost-tab/cost-tab.type';
import { getInitialVisiblePhases } from '../../containers/strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.helpers';
import {
  EpicSortOption,
  FocusMode,
  PerformanceTab,
  SortDirection,
  ThroughputMode,
} from '../../containers/strategy/initiative-performance/initiative-performance.type';
import { RelatedTasksMode } from '../../containers/strategy/initiative-performance/related-tasks-tab/related-tasks-tab.type';
import { MeasureDataType } from '../../containers/strategy/initiative-performance/targets-tab/targets-tab.type';
import { Metric } from '../../containers/strategy/strategy.type';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { isLoggingEnabled, logger } from '../logger/logger';
import {
  CostTabState,
  FlowByPhaseTabState,
  FocusTabState,
  MilestonesTabState,
  OverviewFilters,
  Panel,
  State,
  TargetsTabState,
  ThroughputTabState,
  ThroughputType,
  TreeMapMetric,
  View,
  type RelatedTasksTabState,
} from './strategy-store.type';

const defaultFilters = {
  status: [...STATUS_FILTER_VALUES],
  state: [],
  source: getSourceOptions().map((option) => option.value),
  is_active: [...ACTIVE_STATE_FILTER_VALUES],
  only_bookmarked: false,
  include_empty: false,
  parent_outcomes: [],
  outcome_teams: [],
  committed_next_weeks_enabled: false,
  committed_next_weeks_value: 4,
  epic_due_date_state: [],
  epic_projects: [],
  epic_completion: [],
  epic_target_end_date: [],
  epic_created_date: [],
  objective_status: [],
};

const defaultOverviewFilters: OverviewFilters = {
  mode_bookmarked: false,
  mode_committed: false,
  mode_include_empty: false,
  mode_is_active_only: false,
  mode_show_hidden: false,
  selected_statuses: [],
  committed_next_weeks_value: 4,
  view_mode: 'outcomes',
};

/**
 * Persist migration for `useStrategyStore`. Backfills missing filter fields with
 * safe defaults so users on stale localStorage shapes (any pre-v10 version) load
 * cleanly. Exported so it can be unit-tested without touching the persist layer.
 *
 * The guards below are written to be version-agnostic, but zustand only INVOKES
 * this function when the persisted version differs from the configured `version`.
 * So adding a new filter key here is not enough on its own — the `version` in the
 * persist config must be bumped in the same change, or existing users skip the
 * migration entirely and hydrate the new key as `undefined` (there is no custom
 * `merge`, so the persisted `filters` object replaces the initial one wholesale).
 *
 * @param {unknown} persistedState - Raw shape pulled from localStorage; may be undefined
 * @returns {State} A state object with all required filter fields populated
 */
const migrateStrategyStore = (persistedState: unknown): State => {
  const state = (persistedState ?? {}) as Partial<State>;

  // Migration strategy: Ensure all required filter fields exist with proper defaults
  // This approach is version-agnostic and handles cases where users have incomplete
  // localStorage data from before certain filter fields (state, only_bookmarked) were added.
  // This prevents "Cannot read properties of undefined" errors in production.

  // Ensure filters object exists
  if (!state.filters) {
    state.filters = defaultFilters;
  }

  // Ensure all required filter fields exist with proper defaults
  if (!state.filters.status) {
    state.filters.status = [...STATUS_FILTER_VALUES];
  }
  if (!state.filters.source) {
    state.filters.source = getSourceOptions().map((option) => option.value);
  }
  if (!state.filters.state) {
    state.filters.state = [];
  }
  if (!state.filters.is_active) {
    state.filters.is_active = [...ACTIVE_STATE_FILTER_VALUES];
  }
  if (state.filters.only_bookmarked === undefined) {
    state.filters.only_bookmarked = false;
  }
  if (state.filters.include_empty === undefined) {
    state.filters.include_empty = false;
  }
  if (!state.filters.parent_outcomes) {
    state.filters.parent_outcomes = [];
  }
  // `Array.isArray` rather than a truthiness check: a corrupted or rolled-back blob can
  // hold a string/object/number here, which a truthy guard would pass straight through to
  // consumers that call `.length` and `.includes` on it — substring-matching at best, a
  // TypeError and (with no ErrorBoundary in `src/`) a blank app at worst.
  if (!Array.isArray(state.filters.outcome_teams)) {
    state.filters.outcome_teams = [];
  }
  if (state.filters.committed_next_weeks_enabled === undefined) {
    state.filters.committed_next_weeks_enabled = false;
  }
  if (state.filters.committed_next_weeks_value === undefined) {
    state.filters.committed_next_weeks_value = 4;
  }
  if (!state.filters.epic_due_date_state) {
    state.filters.epic_due_date_state = [];
  }
  if (!state.filters.epic_projects) {
    state.filters.epic_projects = [];
  }
  if (!state.filters.epic_target_end_date) {
    state.filters.epic_target_end_date = [];
  }
  if (!state.filters.epic_created_date) {
    state.filters.epic_created_date = [];
  }

  // Migrate overview filters to new structure. Spread defaults first so any
  // future field auto-extends here without an explicit migration step.
  if (!state.overviewFilters) {
    state.overviewFilters = {
      ...defaultOverviewFilters,
      mode_bookmarked: (state.filters as any).only_bookmarked ?? defaultOverviewFilters.mode_bookmarked,
      mode_committed: (state.filters as any).committed_next_weeks_enabled ?? defaultOverviewFilters.mode_committed,
      selected_statuses: (state.filters as any).selected_statuses ?? defaultOverviewFilters.selected_statuses,
      committed_next_weeks_value:
        (state.filters as any).committed_next_weeks_value ?? defaultOverviewFilters.committed_next_weeks_value,
      view_mode: (state.filters as any).view_mode ?? defaultOverviewFilters.view_mode,
    };
  }

  // Ensure mode_include_empty exists for existing users
  if (state.overviewFilters && state.overviewFilters.mode_include_empty === undefined) {
    state.overviewFilters.mode_include_empty = false;
  }

  // Ensure mode_is_active_only exists for existing users
  if (state.overviewFilters && state.overviewFilters.mode_is_active_only === undefined) {
    state.overviewFilters.mode_is_active_only = false;
  }

  // Ensure mode_show_hidden exists and is boolean for existing users.
  // Use `??` so null/undefined both coerce to false, defending against
  // hand-edited or corrupted localStorage payloads.
  if (state.overviewFilters) {
    state.overviewFilters.mode_show_hidden = state.overviewFilters.mode_show_hidden ?? false;
  }

  // Clean up view_mode from filters as it's now only in overviewFilters
  if (state.filters) {
    delete (state.filters as any).view_mode;
  }

  return state as State;
};

const useStrategyStore = create<State>()(
  devtools(
    persist(
      logger((_) => ({
        initiatives: [],
        kpiOverviewData: null,
        view: View.Table,
        page: 1,
        selectedEpic: null,
        selectedInitiative: null,
        selectedGoal: null,
        selectedOutcome: null,
        filters: defaultFilters,
        overviewFilters: defaultOverviewFilters,
        tableSortBy: 'name',
        tableSortDirection: SortDirection.Asc,
        collapsedGroups: {
          status: false,
          state: false,
          source: false,
          is_active: false,
          only_bookmarked: false,
          objective_status: false,
        },
        activePanel: Panel.Filters,
        searchTerm: '',
        treeMapMetric: TreeMapMetric.CostByInitiative,
        tableGroupBy: null,
        epics: [],
        initiativePerformance: null,
        performanceTab: PerformanceTab.Throughput,
        performanceActivePanel: Panel.Filters,
        throughputMode: ThroughputMode.InitiativeProgress,
      })),
      {
        name: 'strategy-storage',
        storage: createSafeJSONStorage(() => localStorage),
        version: 10,
        migrate: migrateStrategyStore,
        partialize: (state) =>
          Object.fromEntries(
            Object.entries(state).filter(
              ([key]) =>
                ![
                  'activePanel',
                  'page',
                  'searchTerm',
                  'performanceTab',
                  'selectedInitiative',
                  'selectedGoal',
                  'selectedOutcome',
                  'initiatives',
                  'epics',
                  'initiativePerformance',
                  // 'message' state was removed; exclude it so any stale key from an
                  // existing user's persisted blob is dropped on the next write (GC).
                  'message',
                ].includes(key),
            ),
          ),
      },
    ),
    {
      name: 'Bloomfilter - Strategy Store',
      enabled: isLoggingEnabled,
    },
  ),
);

const throughputTabInitialState: ThroughputTabState = {
  mode: ThroughputMode.InitiativeProgress,
  metric: Metric.Tasks,
  selectedEpics: [],
  selectedTeams: [],
  selectedEpic: null,
  selectedTeam: null,
  selectedInitiatives: [],
  selectedOutcomes: [],
  selectedInitiative: null,
  selectedOutcome: null,
  selectedDate: null,
  visibleThroughputTypes: {
    [ThroughputType.Total]: true,
    [ThroughputType.Completed]: true,
  },
};

const useThroughputTabStore = create<ThroughputTabState>()(
  devtools(
    logger((_) => ({
      ...throughputTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Throughput Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const focusTabInitialState: FocusTabState = {
  mode: FocusMode.ContributingTeams,
  metric: Metric.Tasks,
  selectedTeams: [],
  selectedBoards: [],
  focusTeamsData: null,
  focusBoardsData: null,
};

const useFocusTabStore = create<FocusTabState>()(
  devtools(
    logger((_) => ({
      ...focusTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Focus Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const flowByPhaseTabInitialState: FlowByPhaseTabState = {
  metric: Metric.Tasks,
  selectedDate: null,
  selectedEpic: null,
  visiblePhases: getInitialVisiblePhases(),
  data: null,
  phases: null,
  filterControls: {
    searchTerm: '',
    sortBy: EpicSortOption.ID,
    sortDirection: SortDirection.Asc,
    hideEmpty: false,
  },
  useGroups: true,
  selectedSubGroups: new Set(),
};

const useFlowByPhaseTabStore = create<FlowByPhaseTabState>()(
  devtools(
    logger((_) => ({
      ...flowByPhaseTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Flow By Phase Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const targetsTabInitialState: TargetsTabState = {
  targets: [],
  selectedTarget: null,
  availableMeasures: [],
  measureData: null,
  visibleMeasures: {
    [MeasureDataType.Actual]: true,
    [MeasureDataType.Target]: true,
  },
};

const useTargetsTabStore = create<TargetsTabState>()(
  devtools(
    logger((_) => ({
      ...targetsTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Targets Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const milestonesTabInitialState: MilestonesTabState = {
  milestones: [],
  selectedMilestone: null,
  metric: Metric.Tasks,
  visibleThroughputTypes: {
    [ThroughputType.Total]: true,
    [ThroughputType.Completed]: true,
  },
};

const useMilestonesTabStore = create<MilestonesTabState>()(
  devtools(
    logger((_) => ({
      ...milestonesTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Milestones Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const costTabInitialState: CostTabState = {
  mode: CostMode.ContributingEpics,
  selectedEpics: [],
  selectedTeams: [],
  selectedInitiatives: [],
  filterControls: {
    searchTerm: '',
    sortBy: EpicSortOption.ID,
    sortDirection: SortDirection.Asc,
    hideEmpty: false,
  },
  selectedEpic: null,
  selectedTeam: null,
  selectedInitiative: null,
};

const useCostTabStore = create<CostTabState>()(
  devtools(
    logger((_) => ({
      ...costTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Cost Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

const relatedTasksTabInitialState: RelatedTasksTabState = {
  mode: RelatedTasksMode.ContributingTasks,
  selectedEpic: null,
  selectedTeam: null,
  selectedInitiative: null,
  selectedOutcome: null,
  selectedDate: null,
  selectedTypes: [],
  selectedPhase: null,
  searchTerm: '',
  page: 1,
  selectedTask: null,
  filterControls: {
    searchTerm: '',
    sortBy: EpicSortOption.ID,
    sortDirection: SortDirection.Asc,
    hideEmpty: false,
  },
  selectedThroughput: 'total',
  tasks: [],
  useGroups: true,
};

const useRelatedTasksTabStore = create<RelatedTasksTabState>()(
  devtools(
    logger((_) => ({
      ...relatedTasksTabInitialState,
    })),
    {
      name: 'Bloomfilter - Strategy Store - Related Tasks Tab',
      enabled: isLoggingEnabled,
    },
  ),
);

export {
  costTabInitialState,
  defaultFilters,
  defaultOverviewFilters,
  migrateStrategyStore,
  flowByPhaseTabInitialState,
  focusTabInitialState,
  relatedTasksTabInitialState,
  targetsTabInitialState,
  throughputTabInitialState,
  useCostTabStore,
  useFlowByPhaseTabStore,
  useFocusTabStore,
  useMilestonesTabStore,
  useRelatedTasksTabStore,
  useStrategyStore,
  useTargetsTabStore,
  useThroughputTabStore,
};
